import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PiCalendarBlankBold } from 'react-icons/pi';
import { Badge, LoadingState, PageHeader, Text } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import ContainerDetailModal from '@v2/features/container-calendar/ContainerDetailModal';
import EventMonthCalendar from '@v2/features/container-calendar/EventMonthCalendar';
import type { CalendarEvent } from '@v2/features/container-calendar/types';

type ApiResponse = {
    events: CalendarEvent[];
    meta: {
        start: string;
        end: string;
        total: number;
        stage_focus: string;
        task_hint: string;
    };
};

function monthKey(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const legend = [
    { color: 'sky', label: 'ETA previsto' },
    { color: 'amber', label: 'Chegada em breve' },
    { color: 'emerald', label: 'Já chegou' },
    { color: 'rose', label: 'ETA vencido' },
    { color: 'violet', label: 'ETD (saída)' },
] as const;

export default function ContainerCalendarPage() {
    const { t } = useI18n();
    const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [selected, setSelected] = useState<CalendarEvent | null>(null);

    const { data, isLoading } = useQuery<ApiResponse>({
        queryKey: ['calendar', 'containers', monthKey(month)],
        queryFn: async () =>
            (await api.get('/calendar/containers', { params: { month: monthKey(month) } })).data,
    });

    const stats = useMemo(() => {
        const events = data?.events ?? [];
        return {
            total: events.length,
            eta: events.filter((e) => e.event_type === 'eta').length,
            etd: events.filter((e) => e.event_type === 'etd').length,
        };
    }, [data?.events]);

    return (
        <div className="space-y-6">
            <PageHeader
                title={t('v2.calendar.title')}
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: t('v2.calendar.title') },
                ]}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <Link
                        to="/finance/tv"
                        className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
                    >
                        Painel Financeiro (TV)
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <PiCalendarBlankBold className="size-4" />
                        <span>{t('v2.calendar.subtitle')}</span>
                    </div>
                </div>
            </PageHeader>

            <div className="flex flex-wrap items-center gap-3">
                <Badge color="info" variant="flat" rounded="pill">
                    {t('v2.calendar.stage')}: {data?.meta.stage_focus ?? 'transito'}
                </Badge>
                <Text className="text-xs text-gray-500">{data?.meta.task_hint}</Text>
                {!isLoading && (
                    <Text className="text-xs text-gray-600 dark:text-gray-400">
                        {stats.total} eventos ({stats.eta} ETA · {stats.etd} ETD)
                    </Text>
                )}
            </div>

            <div className="flex flex-wrap gap-3 text-xs">
                {legend.map((item) => (
                    <span key={item.color} className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <span
                            className={`inline-block size-3 rounded border ${
                                item.color === 'sky'
                                    ? 'bg-sky-200 border-sky-400'
                                    : item.color === 'amber'
                                      ? 'bg-amber-200 border-amber-400'
                                      : item.color === 'emerald'
                                        ? 'bg-emerald-200 border-emerald-400'
                                        : item.color === 'rose'
                                          ? 'bg-rose-200 border-rose-400'
                                          : 'bg-violet-200 border-violet-400'
                            }`}
                        />
                        {item.label}
                    </span>
                ))}
            </div>

            {isLoading ? (
                <LoadingState label={t('v2.calendar.loading')} />
            ) : (
                <EventMonthCalendar
                    month={month}
                    events={data?.events ?? []}
                    onMonthChange={setMonth}
                    onEventClick={setSelected}
                    selectedEventId={selected?.id}
                />
            )}

            <ContainerDetailModal event={selected} onClose={() => setSelected(null)} />
        </div>
    );
}
